/**
 * FuzzyMatchPage — analyst tool to test Tier-1 fuzzy name matching.
 * Helps tune confidence thresholds and validate transliteration / nickname behaviour.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Sparkles, Languages, Zap } from "lucide-react";
import { RudrikLogo } from "../components/RudrikLogo";

const API = `${window.location.origin}/api`;

const BAND_COLORS = {
  EXACT:    "#22c55e",
  HIGH:     "#22d3ee",
  MEDIUM:   "#f59e0b",
  LOW:      "#ef4444",
  NO_MATCH: "#475569",
};

export default function FuzzyMatchPage() {
  const [query, setQuery] = useState("Mehul Choksi");
  const [candidates, setCandidates] = useState(
    "Mehul Bhai Choksi\nM.C. Choksi\nChoksi Mehul\nमेहुल चोकसी\nMehool Choksey\nVijay Mallya"
  );
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const list = candidates.split("\n").map((s) => s.trim()).filter(Boolean);
      const { data } = await axios.post(`${API}/name-match/score`,
        { query, candidates: list }, { withCredentials: true });
      setResults(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Match failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="fuzzy-match-page" style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <RudrikLogo size={40} />
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>
            Tier-1 Fuzzy Name Match
          </h1>
          <p style={{ color: "#94a3b8", fontSize: 13, margin: "4px 0 0" }}>
            Jaro-Winkler · Levenshtein · Token-set · Initials · Devanagari + Arabic transliteration
          </p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <div>
          <Label icon={Search} text="Query name (the customer / applicant)" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="fuzzy-query-input"
            style={inputStyle} placeholder="Mehul Choksi" />
        </div>
        <div>
          <Label icon={Languages} text="Candidate names (one per line)" />
          <textarea value={candidates} onChange={(e) => setCandidates(e.target.value)}
            data-testid="fuzzy-candidates-input" rows={6}
            style={{ ...inputStyle, fontFamily: "ui-monospace, monospace", minHeight: 120 }} />
        </div>
      </div>

      <button onClick={run} disabled={loading || !query.trim()} data-testid="fuzzy-run-btn"
        style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8,
          padding: "10px 20px", fontWeight: 700, cursor: "pointer", display: "inline-flex",
          alignItems: "center", gap: 8, marginBottom: 24 }}>
        <Zap size={14} /> {loading ? "Scoring…" : "Run Tier-1 match"}
      </button>

      {results && (
        <div data-testid="fuzzy-results">
          <div style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 10,
            padding: 14, marginBottom: 14, fontSize: 12 }}>
            <span style={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase",
              letterSpacing: 0.6 }}>Normalized query: </span>
            <span style={{ color: "#22d3ee", fontFamily: "monospace", marginRight: 14 }}>
              "{results.normalized_query}"
            </span>
            <span style={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase",
              letterSpacing: 0.6 }}>Transliterated: </span>
            <span style={{ color: "#cbd5e1", fontFamily: "monospace" }}>
              "{results.transliterated_query}"
            </span>
          </div>

          {results.results.map((r, i) => (
            <div key={i} data-testid={`fuzzy-result-${i}`}
              style={{ display: "grid", gridTemplateColumns: "100px 1fr auto",
              alignItems: "center", gap: 16,
              background: "#0d1117", border: `1px solid ${BAND_COLORS[r.confidence_band]}44`,
              borderRadius: 10, padding: 14, marginBottom: 8 }}>
              <Band label={r.confidence_band} score={r.score} />
              <div>
                <div style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 700 }}>
                  {r.candidate}
                </div>
                <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 4 }}>
                  Strongest signal: <span style={{ color: "#22d3ee", fontWeight: 700 }}>
                    {r.basis.replace("_", " ")}
                  </span> · normalized: <span style={{ fontFamily: "monospace", color: "#cbd5e1" }}>
                    "{r.normalized.candidate}"
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, fontSize: 10 }}>
                <Pill label="JW" value={r.breakdown.jaro_winkler} />
                <Pill label="Lev" value={r.breakdown.levenshtein} />
                <Pill label="TSet" value={r.breakdown.token_set} />
                <Pill label="Init" value={r.breakdown.initial_expansion} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Label({ icon: Icon, text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
      color: "#64748b", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: 0.6 }}>
      <Icon size={11} /> {text}
    </div>
  );
}

function Band({ label, score }) {
  const color = BAND_COLORS[label] || "#475569";
  return (
    <div style={{ background: `${color}1a`, border: `1px solid ${color}66`,
      borderRadius: 8, padding: 10, textAlign: "center" }}>
      <div style={{ color, fontSize: 22, fontWeight: 800, lineHeight: 1,
        fontFamily: "ui-monospace, monospace" }}>{score}</div>
      <div style={{ color, fontSize: 9, fontWeight: 800, textTransform: "uppercase",
        letterSpacing: 0.6, marginTop: 4 }}>{label.replace("_", " ")}</div>
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6,
      padding: "4px 8px", textAlign: "center", minWidth: 40 }}>
      <div style={{ color: "#64748b", fontSize: 8, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color: value >= 80 ? "#22c55e" : value >= 60 ? "#f59e0b" : "#64748b",
        fontSize: 12, fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>{value}</div>
    </div>
  );
}

const inputStyle = {
  width: "100%", background: "#080c12", border: "1px solid #1e2530",
  borderRadius: 8, padding: "10px 14px", color: "#f1f5f9", fontSize: 13,
  fontFamily: "inherit",
};
